<template>
  <div ref="root" class="gms">
    <button type="button" class="form-select form-select-sm gms-toggle" @click="toggle">
      {{ buttonLabel }}
    </button>

    <div v-if="open" class="gms-menu shadow">
      <input
        ref="searchInput"
        v-model="search"
        type="text"
        class="form-control form-control-sm mb-2"
        placeholder="ค้นหารหัส / ชื่อเกม"
      />
      <div class="text-xs mb-1">
        <button type="button" class="gms-link" @click="selectFiltered">เลือกทั้งหมด{{ search ? " (ที่ค้นเจอ)" : "" }}</button>
      </div>
      <div class="gms-list">
        <label v-for="g in filteredOptions" :key="g.code" class="gms-item">
          <input v-model="draft" type="checkbox" :value="g.code" />
          <span class="gms-label">{{ g.code }} {{ g.name }}</span>
        </label>
        <div v-if="!filteredOptions.length" class="text-sm text-muted text-center py-2">ไม่พบเกม</div>
      </div>
      <div class="d-flex justify-content-between align-items-center mt-2">
        <span class="text-xs text-muted">{{ draft.length ? `เลือก ${draft.length} เกม` : "ทุกเกม" }}</span>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="!draft.length" @click="clearAll">
            ล้างทุกเกม
          </button>
          <button type="button" class="btn btn-sm btn-primary mb-0" @click="apply">ตกลง</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  options: { type: Array, default: () => [] }, // [{ code, name }]
});
const emit = defineEmits(["update:modelValue", "change"]);

const root = ref(null);
const searchInput = ref(null);
const open = ref(false);
const search = ref("");
const draft = ref([]);

const buttonLabel = computed(() => {
  const v = props.modelValue;
  if (!v.length) return "เกม: ทุกเกม";
  if (v.length === 1) {
    const g = props.options.find((o) => o.code === v[0]);
    return `เกม: ${v[0]}${g?.name ? ` ${g.name}` : ""}`;
  }
  if (v.length === 2) return `เกม: ${v.join(", ")}`;
  return `เกม: ${v.length} เกม`;
});

const filteredOptions = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return props.options;
  return props.options.filter(
    (g) => String(g.code).toLowerCase().includes(q) || String(g.name ?? "").toLowerCase().includes(q)
  );
});

function toggle() {
  if (open.value) apply();
  else {
    draft.value = [...props.modelValue];
    search.value = "";
    open.value = true;
    nextTick(() => searchInput.value?.focus());
  }
}

// ล้างเกมที่เลือกทั้งหมด (= ทุกเกม) แล้วใช้ทันที
function clearAll() {
  draft.value = [];
  apply();
}

function selectFiltered() {
  const set = new Set(draft.value);
  filteredOptions.value.forEach((g) => set.add(g.code));
  draft.value = [...set];
}

// เลือกเสร็จค่อยส่งค่าออกไปครั้งเดียว (กดตกลง / คลิกนอกกล่อง) ไม่ยิง API ทุกครั้งที่ติ๊ก
function apply() {
  open.value = false;
  const next = [...draft.value].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  const changed = next.length !== props.modelValue.length || next.some((c, i) => c !== props.modelValue[i]);
  if (changed) {
    emit("update:modelValue", next);
    emit("change", next);
  }
}

function onClickOutside(e) {
  if (open.value && root.value && !root.value.contains(e.target)) apply();
}

onMounted(() => document.addEventListener("mousedown", onClickOutside));
onBeforeUnmount(() => document.removeEventListener("mousedown", onClickOutside));
</script>

<style scoped>
.gms {
  position: relative;
}
.gms-toggle {
  min-width: 150px;
  max-width: 240px;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.gms-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 1050;
  width: 300px;
  padding: 10px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
}
.gms-list {
  max-height: 280px;
  overflow-y: auto;
}
.gms-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: normal;
  cursor: pointer;
}
.gms-item:hover {
  background: #f5f7fa;
}
.gms-item input {
  flex-shrink: 0;
}
.gms-label {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.gms-link {
  padding: 0;
  border: none;
  background: none;
  color: #1a73e8;
  text-decoration: underline;
}
/* dark mode ของ Argon (.dark-version) บังคับ label เป็นสีขาว → กล่องต้องเป็นพื้นเข้มตาม theme */
:global(.dark-version .gms-menu) {
  background: #111c44;
  border-color: rgba(255, 255, 255, 0.15);
}
:global(.dark-version .gms-item:hover) {
  background: rgba(255, 255, 255, 0.08);
}
:global(.dark-version .gms-link) {
  color: #74a7ff;
}
</style>
